import React from 'react';
import { Theme, Box, Grid, Typography } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { connect, useDispatch } from 'react-redux';
import { Languages, OrderProduct, OrderObject, CurrencyObject } from 'common/types';
import ShopperCardShoppingCart from 'components/ShopperCardShoppingCart';
import { updateActiveShopperId } from 'actions';
import { useTranslation } from 'services/localization/useTranslation';
import { PriceBreakdown } from 'common/components/molecules/PriceBreakdown';
import moment from 'moment-timezone';
import {
	getEarliestProductStartTime,
	getLatestProductEndTime,
} from 'common/utils/dateCalculations';
import useShopFormat from 'hooks/useShopFormat';
import { usePermissions } from 'hooks/usePermissions';
import OverbookedBanner from 'components/OverbookedBanner';

interface OwnProps {
	activeRental: OrderObject;
	newProducts: OrderProduct[];
	lang: Languages;
	activeShopperId: string;
	currency: CurrencyObject;
	newPerson?: {
		handleContinue: (productsToAdd: OrderProduct[]) => void;
		productsToAdd: OrderProduct[];
	};
	overbookedVariantIds: string[];
}

interface DispatchProps {
	updateActiveShopperId: (shopperId: string) => void;
}

type Props = OwnProps & DispatchProps & WithStyles<typeof styles>;

export const ShoppingCartForUpdating = (props: Props) => {
	const {
		classes,
		activeRental,
		lang,
		newProducts,
		activeShopperId,
		currency,
		newPerson,
		overbookedVariantIds,
	} = props;
	const shoppers = activeRental.shoppers;
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { localFormat } = useShopFormat();

	const { hasPermission } = usePermissions();

	const allowOverbookings = hasPermission('allow_overbookings');
	const showBanner = !!overbookedVariantIds.length;
	const pricing = activeRental.rentalInfo.pricing;
	const newProductIds = newProducts.map((p) => p.id);
	const oldRentalProducts = activeRental.products.filter((p) => !newProductIds.includes(p.id));

	const activeShoppersProducts = !newPerson
		? activeRental.products.filter((p) => p.shopperId === activeShopperId)
		: activeRental.products;
	const start =
		activeRental.rentalInfo.rentalState === 'ACTIVE'
			? moment().toISOString()
			: getEarliestProductStartTime(activeShoppersProducts) || '';
	const end: string = getLatestProductEndTime(activeShoppersProducts) || '';

	const renderShopperCards = () => {
		return shoppers.map((shopper, index) => {
			return (
				<div key={index}>
					<ShopperCardShoppingCart
						shopper={shopper}
						rentalProducts={oldRentalProducts}
						productsToBeConfirmed={newProducts}
						activeShopperId={activeShopperId}
						currency={currency}
						index={index}
						lang={lang}
						changeActiveShopperId={(shopperId: string) =>
							dispatch(updateActiveShopperId(shopperId))
						}
					/>
				</div>
			);
		});
	};

	const renderRentalPeriod = () => {
		return (
			<Grid container className={classes.durationWrapper}>
				<Box p={1} pl={0}>
					<Typography variant="subtitle2" className={classes.duration}>
						{t('common:times.duration')}
						{':'}
					</Typography>
				</Box>
				<Box p={1}>
					<Box className={classes.duration}>
						<Typography>
							{`${
								activeRental.rentalInfo.rentalState === 'ACTIVE'
									? t('common:times.now', 'Now') + ' -'
									: localFormat(start, 'MMM DD, HH:mm')
							} - 
						`}
						</Typography>
					</Box>
					<Box>
						<Typography>{`${localFormat(end, 'MMM DD, HH:mm')}`}</Typography>
					</Box>
				</Box>
			</Grid>
		);
	};

	return (
		<>
			<div className={classes.cartView}>
				<div className={classes.cartContent}>
					{renderRentalPeriod()}
					{showBanner && (
						<Box ml={-2} mr={-2}>
							<OverbookedBanner
								allowOverbookings={allowOverbookings}
								rentalProducts={newProducts}
								overbookedVariantIds={overbookedVariantIds}
								styling={{ link: true }}
							/>
						</Box>
					)}
					<div className={classes.cardsContainer}>{renderShopperCards()}</div>
				</div>
				<Box className={classes.pricingSection}>
					<PriceBreakdown pricing={pricing} t={t} shopCurrency={currency} lang={lang} />
				</Box>
			</div>
		</>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		cartView: {
			display: 'flex',
			flexDirection: 'column',
			flex: '1 1 auto',
			padding: theme.spacing(0, 2, 0, 2),
			borderTop: '1px solid rgba(0,0,0,0.05)',
			maxHeight: 'calc(100% - 56px)', // fix for mozilla
		},
		marginBottom: {
			marginBottom: 8,
		},
		cartContent: {
			display: 'flex',
			flexDirection: 'column',
			flex: '1 1 auto',
			paddingBottom: 8,
			maxHeight: 'calc(100% - 68px)', // fix for mozilla
		},
		durationWrapper: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'space-between',
		},
		duration: {
			fontSize: theme.spacing(1.75),
		},
		cardsContainer: {
			overflow: 'auto',
			marginLeft: -16,
			marginRight: -16,
			marginBottom: 8,
			maxHeight: '100%',
		},
		pricingSection: {
			padding: theme.spacing(2, 0),
			borderTop: '1px solid rgba(0,0,0,0.05)',
		},
	});

const dispatchProps: DispatchProps = {
	updateActiveShopperId,
};

export default withStyles(styles)(connect(null, dispatchProps)(ShoppingCartForUpdating));
